import moment from 'moment';

/**
 * Date formatting for the events pages.
 *
 * `moment` is never given a locale anywhere in this app, so its own `dddd` and
 * `MMMM` render "Friday" and "September" on a page that is otherwise entirely
 * in Vietnamese. Everything below spells the few words we actually need rather
 * than pulling in moment's locale bundles for two of them.
 */

const WEEKDAYS = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];

/** "Thứ sáu". Short enough to sit under a two-digit day in the date chip. */
export const weekdayOf = (date: any): string => WEEKDAYS[moment(date).day()];

/** "Th 9" -- the label under the day number, kept narrow on purpose. */
export const shortMonthOf = (date: any): string => `Th ${moment(date).month() + 1}`;

/** "Tháng 9 năm 2026", the heading a group of rows sits under. */
export const monthHeadingOf = (date: any): string => {
    const m = moment(date);
    return `Tháng ${m.month() + 1} năm ${m.year()}`;
};

/** The key rows are grouped by: same month of the same year. */
export const monthKeyOf = (date: any): string => moment(date).format('YYYY-MM');

export const formatDay = (date: any): string => (date ? moment(date).format('DD/MM/YYYY') : '--');

/** The lunar date reads as a day and a month; its year is rarely the point. */
export const formatLunarShort = (date: any): string =>
    date ? `${moment(date).format('D/M')} âm lịch` : '';

/**
 * The organisations an event belongs to, in the order the family names them:
 * chi before phái before nhánh. Returns the names only -- the row shows them as
 * one dot-separated line rather than as four mostly-empty table columns.
 */
export const organizationNames = (organizations: any): string[] => {
    if (!Array.isArray(organizations)) return [];
    const byType = (type: string) =>
        organizations.find((o: any) => o?.organizationType === type)?.organizationName;
    return ['Clan', 'Lineage', 'Branch'].map(byType).filter(Boolean);
};

type MonthGroup = { key: string; heading: string; items: any[] };

/**
 * Rows grouped into months, oldest first.
 *
 * The API returns events ordered by when the record was *created*, which is why
 * the list used to show September before July before October. A reader
 * scanning for the next giỗ needs them in date order.
 */
export const groupByMonth = (records: any[]): MonthGroup[] => {
    const groups = new Map<string, MonthGroup>();
    [...records]
        .sort((a, b) => moment(a?.fromDate).valueOf() - moment(b?.fromDate).valueOf())
        .forEach((item) => {
            const key = monthKeyOf(item?.fromDate);
            if (!groups.has(key)) {
                groups.set(key, { key, heading: monthHeadingOf(item?.fromDate), items: [] });
            }
            groups.get(key)!.items.push(item);
        });
    return Array.from(groups.values());
};
